import { useQuery } from "@tanstack/react-query";
import { api } from "./queries";
import type { RegistryCatalog, RegistryPayload, TagSet } from "./types";

/** Catalogs in display order, with the TagSet field each one fills. */
export const CATALOGS: { catalog: RegistryCatalog; field: keyof TagSet; label: string }[] = [
  { catalog: "feature", field: "features", label: "Feature" },
  { catalog: "hardware", field: "hardware", label: "Hardware" },
  { catalog: "software", field: "software", label: "Software" },
  { catalog: "program", field: "programs", label: "Program" },
];

export const EMPTY_TAGS: TagSet = { features: [], hardware: [], software: [], programs: [] };

/**
 * The pinned qa-product-registry vocabulary. The payload only changes when the
 * backend re-pins, so it is fetched once per session and never refetched.
 */
export function useRegistry() {
  return useQuery({
    queryKey: ["registry"],
    queryFn: api.getRegistry,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/** Display name for a key, falling back to the key while the registry loads. */
export function displayName(registry: RegistryPayload | undefined, key: string): string {
  if (!registry) return key;
  for (const { catalog } of CATALOGS) {
    const hit = registry.catalogs[catalog].find((e) => e.key === key);
    if (hit) return hit.name;
  }
  return key;
}

/** The catalog a key belongs to, or undefined while the registry loads / for an unknown key. */
export function catalogOf(
  registry: RegistryPayload | undefined,
  key: string,
): RegistryCatalog | undefined {
  if (!registry) return undefined;
  for (const { catalog } of CATALOGS) {
    if (registry.catalogs[catalog].some((e) => e.key === key)) return catalog;
  }
  return undefined;
}

/**
 * One-click product areas, each a shortcut that fills registry keys from any catalog.
 * Presentation only — a run always stores the keys themselves, so the raw key list
 * below the picker stays authoritative. "Firmware" is the FIRMWARE_UPDATES capability;
 * add the device key(s) (V3, VUE, …) too when a run is for specific hardware.
 */
export const PRODUCT_AREA_PRESETS: { label: string; keys: string[] }[] = [
  { label: "Consumer App", keys: ["APP"] },
  { label: "Consumer Web", keys: ["WEB"] },
  { label: "Admin App", keys: ["ADMIN"] },
  { label: "Invite2Charge", keys: ["I2CHG"] },
  { label: "Installer", keys: ["INSTALLER"] },
  { label: "ARC / Partner", keys: ["PARTNER"] },
  { label: "Inhab", keys: ["INHAB"] },
  { label: "Cloud", keys: ["SERVER"] },
  { label: "Firmware", keys: ["FIRMWARE_UPDATES"] },
  { label: "QA Mesh", keys: ["QA-MESH"] },
];

export function hasTags(tags: TagSet | undefined): boolean {
  return Boolean(tags && CATALOGS.some(({ field }) => tags[field].length > 0));
}

/** A single-key tag filter, as the dashboard's filter bars use it. */
export type TagFilterValue = { catalog: RegistryCatalog; key: string };

export const NO_TAG_FILTER: TagFilterValue = { catalog: "feature", key: "" };

/** Adds ``?feature=EXCESS_SOLAR`` (etc.) to a query string when a key is chosen. */
export function applyTagFilter(params: URLSearchParams, filter: TagFilterValue): void {
  if (filter.key) params.set(filter.catalog, filter.key);
}
